import React from "react";
import { useAuth } from "react-oidc-context";
import { ThreeDots } from "react-loader-spinner";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { SuccessCheckIcon } from "../../../icons/SuccessCheckIcon";
import ClockIcon from "../../../icons/ClockIcon";
import {
  IDestinationExtension,
  IQuoteExtensionOld,
  IQuoteExtension,
} from "../../../types/ICompAnalysisQMD";
import { useNitroStore } from "../../../store/Store";
import NitroService from "../../../utils/nitro.service";
import {
  hasProductForQuote,
  MissingTruckFreightDesktopSections,
} from "../components/multiLocationQuoteUtils";
import { IOccupant } from "../../../types/ILocation";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import NitroRequestService from "../../../utils/nitroRequest.service";
import { icons } from "../../../svg";
import { useStickyTableHeader } from "../../../hooks/useStickyTableHeader";

interface TruckMissingOriginsTableProps {
  rows: TableRow[];
  loading: boolean;
  destination: IDestinationExtension;
}

type RowProductStatus = "check" | "add" | "dash";
type RowMilesStatus = "check" | "request" | "requested" | "dash";

export interface TableRow {
  rowKey: string;
  originName: string;
  productStatus?: RowProductStatus;
  milesStatus: RowMilesStatus;
  noGuidance: string | null;
  quote: IQuoteExtensionOld | IQuoteExtension;
}

// TODO: what do we do in case of multiple non koch occupants
// TODO: origin details are NOT always present...
// TODO: truck rows refresh all the time
// TODO: logistics tab doesn't save result
// TODO: new api details for logistics panel
// TODO: requested state doesn't persist on refresh
export const buildRows = (
  sections: MissingTruckFreightDesktopSections,
  product?: string,
): TableRow[] => {
  const rows: TableRow[] = [];

  sections.noRouteOrigins.forEach((q) => {
    rows.push({
      rowKey: `no-route-${q.competitor ? "comp" : "koch"}-${q.origin.id}`,
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      milesStatus: "dash",
      noGuidance: "No Route",
      quote: q,
    });
  });

  sections.missingMilesOrigins.forEach(({ quote: q, mileageRequested }) => {
    rows.push({
      rowKey: `missing-miles-${q.competitor ? "comp" : "koch"}-${q.origin.id}`,
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      milesStatus: mileageRequested ? "requested" : "request",
      noGuidance: null,
      quote: q,
    });
  });

  sections.expiredOrigins.forEach((q) => {
    rows.push({
      rowKey: `no-guidance-${q.competitor ? "comp" : "koch"}-${q.origin.id}`,
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      milesStatus: "check",
      noGuidance: "Expired",
      quote: q,
    });
  });

  sections.outsideMileageBandOrigins.forEach((data) => {
    rows.push({
      rowKey: `outside-mileage-${data.quote.competitor ? "comp" : "koch"}-${
        data.quote.origin.id
      }`,
      originName: data.quote.origin.name,
      productStatus:
        product && hasProductForQuote(data.quote, product) ? "check" : "add",
      milesStatus: "check",
      noGuidance: `Outside Mileage Band\n${data.mileage} mi`,
      quote: data.quote,
    });
  });

  return rows;
};

export const TruckMissingOriginsTableOld: React.FC<
  TruckMissingOriginsTableProps
> = ({ rows, loading, destination }) => {
  const [product, setMultiLocationDestinations] = useNitroStore((state) => [
    state.product,
    state.setMultiLocationDestinations,
  ]);
  const triggerRefetchMultiLocation = useNitroCAStore(
    (state) => state.triggerRefetchMultiLocation,
  );
  const [
    setExpandedDestination,
    setOriginToTroubleshoot,
    setDestinationToTroubleshoot,
    setDisplayAddProductToOriginPanel,
  ] = useNitroCAStore((state) => [
    state.setExpandedDestination,
    state.setOriginToTroubleshoot,
    state.setDestinationToTroubleshoot,
    state.setDisplayAddProductToOriginPanel,
  ]);
  const auth = useAuth();
  const { ref: shellRef, isSticky } = useStickyTableHeader(
    "deskstop-destination-table",
  );
  const [requestedMileageRows, setRequestedMileageRows] = React.useState<
    Set<string>
  >(new Set());
  const [loadingProductRows, setLoadingProductRows] = React.useState<
    Set<string>
  >(new Set());
  const [loadingMileageRows, setLoadingMileageRows] = React.useState<
    Set<string>
  >(new Set());

  const addProductToOccupant = async (
    occupant: IOccupant,
    locationId: string,
  ) => {
    const updatedOccupant = {
      ...occupant,
      products: [...occupant.products, { product, quantity: 0 }],
    } as IOccupant;
    await NitroService.updateOccupants({
      id: locationId,
      occupants: [updatedOccupant],
    });
  };

  const renderProductCell = (
    quote: IQuoteExtensionOld | IQuoteExtension,
    rowKey: string,
  ) => {
    const status: RowProductStatus = hasProductForQuote(quote, product)
      ? "check"
      : "add";
    if (status === "check") return <SuccessCheckIcon />;
    if (status === "add") {
      if (loadingProductRows.has(rowKey)) {
        return <ThreeDots height="20" width="30" color="#4fa94d" />;
      }
      return (
        <button
          type="button"
          className={styles.truckMissingOriginsPill}
          onClick={async () => {
            if (quote.origin.details!.occupants.length === 1) {
              setLoadingProductRows((prev) => new Set([...prev, rowKey]));
              await addProductToOccupant(
                quote.origin.details!.occupants[0],
                quote.origin.id,
              );
              setMultiLocationDestinations([]);
              triggerRefetchMultiLocation();
              setExpandedDestination(
                { id: destination.id, name: destination.name },
                { expandTruckWithNoFreight: true },
              );
            } else {
              setDestinationToTroubleshoot(destination);
              setOriginToTroubleshoot(quote.origin, !quote.competitor);
              setDisplayAddProductToOriginPanel(true);
            }
          }}
          aria-label={`Add ${product}`}
        >
          <span className={styles.truckMissingOriginsPillPlus}>+</span>
          <span>Add</span>
        </button>
      );
    }
    return <span className={styles.truckMissingOriginsDash}>—</span>;
  };

  const renderMilesCell = (
    status: RowMilesStatus,
    quote: IQuoteExtensionOld | IQuoteExtension,
    rowKey: string,
  ) => {
    if (status === "check") return <SuccessCheckIcon />;
    if (status === "requested") {
      return (
        <span className={styles.truckMissingOriginsRequestedPill}>
          <ClockIcon className={styles.truckMissingOriginsRequestedPillIcon} />
          <span>Requested</span>
        </span>
      );
    }
    if (status === "request") {
      if (requestedMileageRows.has(rowKey)) {
        return (
          <span className={styles.truckMissingOriginsRequestedPill}>
            <ClockIcon
              className={styles.truckMissingOriginsRequestedPillIcon}
            />
            <span>Requested</span>
          </span>
        );
      }
      if (loadingMileageRows.has(rowKey)) {
        return <ThreeDots height="20" width="30" color="#4fa94d" />;
      }
      return (
        <button
          type="button"
          className={styles.truckMissingOriginsPill}
          onClick={async () => {
            setLoadingMileageRows((prev) => new Set([...prev, rowKey]));
            try {
              const destinationZip =
                destination.details?.zipCode || destination.zipCode || "";
              await NitroRequestService.postMileageRequest({
                marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
                product,
                mode: "TRUCK",
                origin: quote.origin.name,
                originZipCode: quote.origin.details?.zipCode || "",
                destination: destination.name,
                destinationZipCode: destinationZip,
                comments: "",
              });
              await NitroService.updateMileageRequestStatus({
                originZip: quote.origin.details?.zipCode || "",
                destinationZip,
                user: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
              });
              setRequestedMileageRows((prev) => new Set([...prev, rowKey]));
            } catch (error) {
              console.error(
                "Requests/missingMileageRequest - Failed to send email:",
                error,
              );
            } finally {
              setLoadingMileageRows((prev) => {
                const next = new Set(prev);
                next.delete(rowKey);
                return next;
              });
            }
          }}
          aria-label="Request Miles"
        >
          <span className={styles.truckMissingOriginsPillPlus}>+</span>
          <span>Request</span>
        </button>
      );
    }
    return <span className={styles.truckMissingOriginsDash}>—</span>;
  };

  return (
    <div
      ref={shellRef}
      className={`${styles.truckMissingOriginsTableShell} ${styles.missingOriginsLegacy}`}
      style={{ borderRadius: isSticky ? "0 0 14px 14px" : "14px" }}
    >
      {/* Table header row */}
      <div className={styles.truckMissingOriginsTableHeader}>
        <div className={styles.truckMissingOriginsColOrigin}>ORIGIN</div>

        <div className={styles.truckMissingOriginsColUan}>{product}</div>

        <div
          id="missing-truck-origin-miles-header"
          className={styles.truckMissingOriginsColMiles}
        >
          MILES
        </div>
        <div className={styles.truckMissingOriginsColNoGuidance}>
          NO GUIDANCE
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className={styles.truckMissingOriginsRow}>
          <span className={styles.truckMissingOriginsDash}>Loading…</span>
        </div>
      )}

      {/* Data rows */}
      {!loading &&
        rows.map((row) => (
          <div key={row.rowKey} className={styles.truckMissingOriginsRow}>
            {/* Origin */}
            <div className={styles.truckMissingOriginsColOrigin}>
              <div className={styles.truckMissingOriginsCityCell}>
                <img
                  src={
                    row.quote.competitor ? icons.RedDotIcon : icons.BlueDotIcon
                  }
                  alt="Dot Icon"
                  className={styles.dotIcon}
                />
                <span
                  id={`missing-truck-origin-name-${row.rowKey}`}
                  className={`${styles.originName} ${
                    row.quote.competitor
                      ? styles.originNameRed
                      : styles.originNameBlue
                  }`}
                >
                  {row.originName}
                </span>
              </div>
            </div>

            {/* Product */}
            <div className={styles.truckMissingOriginsColUan}>
              {renderProductCell(row.quote, row.rowKey)}
            </div>

            {/* Miles */}
            <div
              id={`missing-truck-origin-miles-${row.rowKey}`}
              className={styles.truckMissingOriginsColMiles}
            >
              {renderMilesCell(row.milesStatus, row.quote, row.rowKey)}
            </div>

            {/* No Guidance */}
            <div className={styles.truckMissingOriginsColNoGuidance}>
              {row.noGuidance ? (
                <span className={styles.truckMissingOriginsStatusText}>
                  {row.noGuidance}
                </span>
              ) : (
                <span className={styles.truckMissingOriginsDash}>—</span>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};
